import throttle from 'lodash.throttle';
import {
  Dispatch,
  SetStateAction,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useSnapshot } from 'valtio';

import { getHomepageUrl } from '@cartwhl/common/utils';
import Dropdown from '@cartwhl/ui/atoms/Dropdown';
import Loading from '@cartwhl/ui/atoms/Loading';
import Tooltip from '@cartwhl/ui/atoms/Tooltip';
import { TimelineItem } from '@cartwhl/ui/feature/Timeline';
import ThreeDotsIcon from '@cartwhl/ui/icons/ThreeDotsIcon';
import Button from '@cartwhl/ui/input/Button';

import { animationPlayerStore, projectStore, sceneSettingsStore } from 'stores';

export default function VideoOverlay() {
  const { currentTime } = useSnapshot(animationPlayerStore.state);
  const {
    motion: { timelineData },
  } = useSnapshot(projectStore.state);
  const [expanded, setExpanded] = useState(false);

  if (!timelineData) return null;

  const objectID = Object.keys(timelineData)[0];
  const currentItem = timelineData[objectID].find((item) => {
    return (
      currentTime >= item.startValue &&
      currentTime <= item.startValue + item.duration
    );
  });

  if (currentItem?.comicMp4URL) {
    return (
      <SyncedVideo
        key={currentItem.comicMp4URL}
        currentItem={currentItem}
        mediaId={undefined}
        expanded={expanded}
        setExpanded={setExpanded}
      />
    );
  }
}

const SyncedVideo = ({
  currentItem,
  mediaId,
  expanded,
  setExpanded,
}: {
  currentItem: TimelineItem;
  mediaId?: string;
  expanded: boolean;
  setExpanded: Dispatch<SetStateAction<boolean>>;
}) => {
  const { currentTime, trimFrameStart, trimFrameEnd } = useSnapshot(
    animationPlayerStore.state,
  );
  const {
    playbackSettings: { frameRate },
  } = useSnapshot(sceneSettingsStore.state);

  const [canPlayThrough, setCanPlayThrough] = useState(false);
  const [hasError, setHasError] = useState(false);

  const [aspectRatio, setAspectRatio] = useState(1);

  const videoRef = useRef<HTMLVideoElement>(null);

  const throttledFrameUpdate = useMemo(
    () =>
      throttle((time: number) => {
        if (videoRef.current) {
          videoRef.current.currentTime = time;
        }
      }, 41.66), // ~24fps (1000ms/24) - We keep it at 24fps as some browsers cannot handle higher fps
    [],
  );

  const [originalVideoDuration, setOriginalVideoDuration] = useState<
    number | null
  >(null);

  useEffect(() => {
    const updateVideoTime = () => {
      if (!canPlayThrough || !videoRef.current) {
        return;
      }

      const frameTime = 1 / frameRate;
      const stitchStartDelay = currentItem.stitchStartDelay ?? 0;
      const stitchEndDelay = currentItem.stitchEndDelay ?? 0;
      const trimFrameStartTime = (trimFrameStart ?? 0) / frameTime;
      const trimFrameEndTime = (trimFrameEnd ?? 0) / frameTime;

      // Video values:
      const videoStartDelay = stitchStartDelay + trimFrameStartTime;
      const videoEndDelay = stitchEndDelay + trimFrameEndTime;
      const videoDuration = originalVideoDuration
        ? originalVideoDuration - videoStartDelay - videoEndDelay
        : 0;
      const currentItemTime =
        currentTime - currentItem.startValue + videoStartDelay;

      requestAnimationFrame(() => {
        throttledFrameUpdate(currentItemTime);
      });
    };

    updateVideoTime();
  }, [
    canPlayThrough,
    throttledFrameUpdate,
    frameRate,
    trimFrameStart,
    trimFrameEnd,
    currentItem.stitchStartDelay,
    currentItem.stitchEndDelay,
    currentItem.startValue,
    currentTime,
    originalVideoDuration,
  ]);

  if (hasError) {
    // Currently the video returns a 404 if you uncheck the box before upload
    return null;
  }

  return (
    <>
      <div
        className={`pointer-events-none absolute top-0 right-0 z-10 flex justify-end rounded-2xl transition-all ease-linear ${
          expanded
            ? 'h-[220px] w-[220px] sm:h-[316px] sm:w-[316px] xl:h-[440px] xl:w-[440px] 2xl:h-[640px] 2xl:w-[640px]'
            : 'h-[90px] w-[90px] sm:h-[124px] sm:w-[124px] xl:h-[164px] xl:w-[164px] 2xl:h-[200px] 2xl:w-[200px]'
        }`}
      >
        <div
          className="group pointer-events-auto relative h-fit max-h-full w-fit max-w-full"
          style={{
            width: aspectRatio > 1 ? '100%' : `${aspectRatio * 100}%`,
            height: aspectRatio > 1 ? `${100 / aspectRatio}%` : '100%',
          }}
        >
          <video
            key={currentItem.comicMp4URL}
            className="min-h-0 min-w-0 rounded-2xl transition-all"
            ref={videoRef}
            playsInline
            muted
            crossOrigin="use-credentials"
            preload="auto"
            onLoadedMetadata={() => {
              if (videoRef.current) {
                setOriginalVideoDuration(videoRef.current?.duration);
                // Get native video dimensions
                const videoWidth = videoRef.current.videoWidth;
                const videoHeight = videoRef.current.videoHeight;
                const aspectRatio = videoWidth / videoHeight;
                setAspectRatio(aspectRatio);
              }
            }}
            onCanPlayThrough={() => setCanPlayThrough(true)}
            onError={() => setHasError(true)}
          >
            <source src={currentItem.comicMp4URL} type="video/mp4" />
          </video>

          {mediaId && canPlayThrough && (
            <Dropdown
              trigger={
                <Button
                  variant="tertiary"
                  size="sm"
                  className="pointer-events-auto absolute top-0.5 right-0.5 z-20 lg:top-2 lg:right-2 lg:opacity-0 lg:transition-opacity lg:group-hover:opacity-100"
                  icon={<ThreeDotsIcon />}
                />
              }
            >
              <Dropdown.Item
                onClick={() => {
                  window.open(
                    getHomepageUrl(
                      `/profile/uploaded-media?mediaId=${mediaId}`,
                    ),
                    '_blank',
                  );
                }}
              >
                Go to original video
              </Dropdown.Item>
            </Dropdown>
          )}
          <Tooltip
            text="Expand video reference"
            side="left"
            disabled={expanded}
          >
            <button
              className="group-hover:bg-gray-90/20 absolute inset-0 z-10 h-full w-full rounded-2xl transition-colors"
              onClick={() => setExpanded((prev) => !prev)}
            ></button>
          </Tooltip>
        </div>
        {!canPlayThrough && (
          <div className="absolute top-0 right-0 flex h-2/3 w-full items-center justify-center rounded-2xl bg-white/20">
            <Loading className="text-white" />
          </div>
        )}
      </div>
    </>
  );
};
